/* ==========================================================================
   Donaldson BOFA machine page with a system builder, V1 — assets/abi-bofa-unit-page.js
   Loaded (defer) by sections/bofa-unit-page.liquid, template product.bofa-unit-v2. Release 2026-11, feature bofa-unit-page.
   Spec: Design Handoffs/BOFA Product Pages/design_handoff_bofa_machine_page_v1/README.md ("The builder", "States", "Links").

   Everything comes from three islands the section renders:
     data-bup-config    the product: variants (titles, SKUs, prices: always from Shopify), model, quote-only, links
     data-bup-options   the unit's builder options from the generated mapping (structure only; absent → the plain buy box)
     data-bup-products  live price / variant id / availability by SKU for every option that has a store product
   What the script does:
     - the steps (version → hose kit → capture → add-ons → review), one open at a time; required steps gate "Add system to cart";
     - the hose kit from ports × port size × hose length; a choice with no kit stays on screen, disabled, with its reason;
     - the reset rules with their written messages (focus stays on the control just used; one polite live region);
     - "Your system": cart lines, the "Available by quote" group, totals with the reference list price (never struck);
     - Add system to cart = ONE /cart/add.js call with every purchasable line, then the Cart V2 drawer. Quote items never go
       to the cart. A quote-only unit has no cart path at all;
     - "Request a quote for this build": /pages/bofa-quote-request?model=<slug>&build=SKU:qty,… (every chosen line, max 10);
     - the build kept across Back / refresh in sessionStorage, per product handle; the phone bar and summary sheet;
     - a link that names a version (?variant=…) starts with that version, and the address follows the version chosen;
       otherwise nothing is chosen for the customer;
     - a unit with nothing to build with keeps the section's plain buy box; the script only makes its Add to cart open the drawer.
   The page never picks a part for the customer: only what was chosen (or the hose kit a version includes) is in the build.
   No phone number, no savings wording. Text goes in through textContent only (never innerHTML).
   `core` (pure functions) is exported for Node tests.
   ========================================================================== */
(function (factory) {
  'use strict';
  var core = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  if (typeof document === 'undefined') return;
  var start = function () {
    var roots = document.querySelectorAll('[data-bup]');
    for (var i = 0; i < roots.length; i++) core.mount(roots[i]);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})(function () {
  'use strict';

  var CART_TIMEOUT = 15000;
  var MAX_QUOTE_LINES = 10;
  var KIT = { 1: 'single', 2: 'dual', 3: 'triple' };
  var PORTS = { 1: 'One port', 2: 'Two ports', 3: 'Three ports' };
  var STEP_NAME = { version: 'Version', hose: 'Hose kit', capture: 'Capture', addons: 'Add-ons', review: 'Review' };
  var STR = {
    done: 'Done', optional: 'Optional', required: 'Required', last: 'Last step', change: 'Change',
    not_chosen: 'Not chosen yet',
    one_version: 'One version',
    signals: 'remote signals', spigot: 'exhaust spigot', compressor: 'compressor', hose_kit: 'hose kit',
    /* {machine} = "laser" on laser units (the README's wording), "machine" on every other unit */
    hose_lead: 'The hose kit runs from the extractor’s {inlet} inlet to your {machine}’s exhaust port.',
    hose_lead_plain: 'The hose kit runs from the extractor to your {machine}’s exhaust port.',
    hose_lead_pick: ' Pick the kit that matches the number and size of ports on your {machine}.',
    ports_legend: 'How many exhaust ports does your {machine} have?',
    size_legend: 'Exhaust port size on your {machine}',
    size_hint: 'Your {machine}’s manual or spec sheet gives it.',
    length_legend: 'Hose length',
    kit_word: '{Kind} hose kit',
    included: 'Use the included kit', included_sub: 'Standard hose kit, comes with this version',
    included_line: 'Standard hose kit, included',
    own: 'I have hoses', own_sub: 'Nothing added',
    no_kit_listed: 'No {kind} kit is listed for the {model}',
    kit_only: '{Kind} kit: {size} only',
    no_kit: 'No {kind} kit',
    standard_hose: 'Standard hose',
    length_only: 'For {sizes} ports only',
    by_quote: 'By quote', by_quote_lc: 'by quote', on_quote: 'On your quote', available_by_quote: 'Available by quote',
    not_sure: 'Not sure?', not_sure_kit: 'Not sure which kit fits?', contact: 'Send us a message',
    continue_to: 'Continue to {step}',
    capture_default_sub: 'Adds nothing',
    addons_lead: 'Donaldson lists these as compatible with the {model}. Add any you need.',
    ilf_title: 'Inline pre-filter (ILF)', ilf_desc: 'A bag pre-filter stage in the hose line, ahead of the extractor. Choose one size.',
    ilf_none: 'None', ilf_none_sub: 'No inline pre-filter',
    spare_title: 'Spare filter set', spare_title_one: 'Spare filter', spare_title_twin: 'Spare filter set (twin packs)', spare_legend: 'Spare filters',
    spare_desc_one: 'One {a}, the standard version.',
    spare_desc_two: 'One {a} and one {b}, the standard versions.',
    spare_desc: 'The standard versions: {list}.', spare_desc_one_plain: 'The standard version: {a}.',
    filters_note_one: 'The {a} is available by quote. See ',
    filters_note_two: 'The {a} and {b} are available by quote. See ',
    filters_note_many: 'Other filter versions are available by quote. See ',
    filters_link: 'Filters for the {model}',
    review_lead: 'Check your system, then add it to the cart or request a quote.',
    review_lead_quote: 'Check your build, then request a quote for it.',
    review_title: 'Your choices',
    review_none: 'None',
    quote_note: 'The quote form opens with your build filled in. Nothing is sent until you submit it.',
    empty: 'Choose a version to start. Your build shows here as you go.',
    cart_total: 'Cart total', build_total: 'Build total',
    items_cart_one: '1 item in the cart', items_cart: '{n} items in the cart',
    items_quote_only_one: '1 item, at today’s web prices', items_quote_only: '{n} items, at today’s web prices',
    quote_group: 'Available by quote ({n})',
    quote_group_note_one: 'Not added to the cart. Request a quote for it, or for the whole build.',
    quote_group_note: 'Not added to the cart. Request a quote for them, or for the whole build.',
    add: 'Add system to cart', add_plain: 'Add to cart', adding: 'Adding…', quote: 'Request a quote for this build',
    need_version: 'Choose a version in step 1 first.', need_hose: 'Choose a hose kit in step 2 first.',
    unavailable: 'This version can’t be ordered online right now. Request a quote for it.',
    unavailable_unit: 'This unit can’t be ordered online right now. Request a quote for it.',
    added: 'Added to cart: {model} system, {n} items.', added_one: 'Added to cart: {model}.',
    add_failed: 'That didn’t go in the cart. Please try again.',
    list_ref: '{label} {price}',
    from: 'From',
    meta_version: '{title} · {sku}',
    meta_hose: 'Hose kit · {sku}', meta_capture: 'Capture · {sku}', meta_addon: 'Add-on · {sku}', meta_spare: 'Spare filter · {sku}',
    step_now: 'Step {i} of {n} · {name}',
    tile_need: '{name} · required', tile_quote: '{name} · quote',
    figcap: '{model} with {list}',
    bar: 'Your system', bar_cart: '{n} in cart', bar_items: '{n} on your quote', bar_quote: '{n} by quote', bar_empty: 'nothing chosen yet',
    /* reset rules (README "The builder"): the messages as written */
    reset_included_std: 'The standard version doesn’t include a hose kit, so we took the included kit out of your system. Choose a hose kit to finish this step.',
    reset_included: '“{title}” doesn’t include a hose kit, so we took the included kit out of your system. Choose a hose kit to finish this step.',
    reset_size: 'The {kind} hose kit doesn’t come for {size} ports, so we cleared the port size. Choose a size to finish this step.',
    reset_length: 'The {length} hose comes for {sizes} ports only, so we switched to the standard hose.',
    reset_length_kit: 'The {kind} hose kit comes with the standard hose only, so we switched to the standard hose.',
    live_version: 'Version changed to {title}.', live_included_removed: ' Included hose kit removed. Step 2 needs a choice.', live_included_added: ' The included hose kit is in your system.',
    live_size: 'Port size cleared. Step 2 needs a choice.', live_length: 'Hose length changed to the standard hose.',
    live_total: 'Your system: {n} in the cart, {total}.', live_total_quote: 'Your build: {n} on your quote, {total}.', live_by_quote: ' {n} by quote.'
  };

  /* ---------------- pure helpers ---------------- */
  function text(v) { return v == null ? '' : String(v); }
  function trim(v) { return text(v).replace(/^\s+|\s+$/g, ''); }
  function fill(tpl, vars) { return text(tpl).replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? String(vars[k]) : ''; }); }
  function str(key, vars) { return fill(STR[key], vars); }
  function find(list, fn) { for (var i = 0; i < (list ? list.length : 0); i++) if (fn(list[i], i)) return list[i]; return null; }
  function cap(s) { var t = text(s); return t ? t.charAt(0).toUpperCase() + t.slice(1) : t; }
  /* "Clean-room combined filter" → "clean-room …"; a name with its own capitals ("SafeChange", "DeepPleat", "AD Cyclone") stays as it is */
  function lowerFirst(s) { var r = text(s); var w = r.split(' ')[0]; return /^[A-Z][a-z]/.test(r) && !/[A-Z]/.test(w.slice(1)) ? r.charAt(0).toLowerCase() + r.slice(1) : r; }
  function uniq(list) { var out = []; list.forEach(function (x) { if (out.indexOf(x) < 0) out.push(x); }); return out; }
  function has(obj, key) { return !!obj && Object.prototype.hasOwnProperty.call(obj, key); }

  /* Shopify money format ("${{amount}}" and its variants); tags a shop may wrap around it are dropped. */
  function formatMoney(cents, format) {
    var f = text(format || '${{amount}}').replace(/<[^>]*>/g, '');
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(f);
    var kind = m ? m[1] : 'amount';
    function delim(n, precision, thousands, decimal) {
      var fixed = (Number(n) / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var value;
    if (kind === 'amount_no_decimals') value = delim(cents, 0, ',', '.');
    else if (kind === 'amount_with_comma_separator') value = delim(cents, 2, '.', ',');
    else if (kind === 'amount_no_decimals_with_comma_separator') value = delim(cents, 0, '.', ',');
    else if (kind === 'amount_with_apostrophe_separator') value = delim(cents, 2, '’', '.');
    else value = delim(cents, 2, ',', '.');
    return m ? f.replace(m[0], value) : value;
  }

  /* ---------------- the model: product + mapping + live products ---------------- */
  /* An option's store facts. sold = it has an active store variant that can be ordered; anything else is "by quote". */
  function facts(products, sku) {
    var p = sku && has(products, sku) && products[sku] && typeof products[sku] === 'object' ? products[sku] : null;
    if (!p) return { sold: false, price: null, compare_at: null, variant_id: null, image: null, url: null };
    var ok = !!p.available && p.variant_id != null && typeof p.price === 'number';
    return { sold: ok, price: ok ? p.price : null, compare_at: ok && p.compare_at != null && p.compare_at > p.price ? p.compare_at : null, variant_id: ok ? p.variant_id : null, image: p.image || null, url: p.url || null };
  }
  function buildModel(cfg, opts, products) {
    opts = opts && typeof opts === 'object' ? opts : null;
    products = products && typeof products === 'object' ? products : {};
    var mapped = {};
    ((opts && opts.versions) || []).forEach(function (v) { mapped[v.sku] = v; });
    var versions = (cfg.variants || []).map(function (v) {
      var m = has(mapped, v.sku) ? mapped[v.sku] : null;
      return { id: v.id, sku: v.sku, title: v.title, price: v.price, compare_at: v.compare_at != null && v.compare_at > v.price ? v.compare_at : null, available: !!v.available, desc: m ? m.desc : null, includes: (m && m.includes) || {} };
    });
    var line = function (o, kind) {
      var f = facts(products, o.sku);
      return { kind: kind, sku: o.sku, label: o.label, desc: o.desc || null, group: o.group || null, ports: o.ports || null, size: o.size != null ? String(o.size) : null, size_label: o.size_label || null, hose: o.hose || null, note: o.note || null, sold: f.sold, price: f.price, compare_at: f.compare_at, variant_id: f.variant_id, image: f.image, url: f.url };
    };
    var hose = ((opts && opts.hose) || []).map(function (o) { return line(o, 'hose'); });
    var capture = ((opts && opts.capture) || []).map(function (o) { return line(o, 'capture'); });
    var addons = ((opts && opts.addons) || []).map(function (o) { return line(o, 'addon'); });
    var spare = ((opts && opts.spare) || []).map(function (o) { return line(o, 'spare'); });
    /* a set goes in the cart whole or not at all: one filter not sold online puts the whole set on the quote */
    if (spare.some(function (f) { return !f.sold; })) spare.forEach(function (f) { f.sold = false; f.price = null; f.compare_at = null; f.variant_id = null; });
    var steps = ['version'];
    if (hose.length) steps.push('hose');
    if (capture.length) steps.push('capture');
    if (addons.length || spare.length) steps.push('addons');
    steps.push('review');
    return {
      cfg: cfg, name: cfg.name, quoteOnly: !!cfg.quote_only,
      versions: versions, hose: hose, hoseNone: !!(opts && opts.hose_none), capture: capture, captureDefault: (opts && opts.capture_default) || null,
      addons: addons, spare: spare, filtersByQuote: (opts && opts.filters_by_quote) || [],
      steps: steps,
      /* nothing to build with: the README's plain buy box (version, Add to cart, Request a quote) */
      plain: steps.length === 2
    };
  }

  /* ---------------- state ---------------- */
  function noHose() { return { mode: null, ports: null, size: null, length: null, pick: null }; }
  function emptyState(model) {
    var s = { version: null, hose: noHose(), capture: null, addons: {}, ilf: null, spare: false, passed: {}, open: 'version', note: null };
    /* one version: it is the choice */
    if (model.versions.length === 1) { s.version = model.versions[0].sku; s.passed.version = true; s.open = model.steps[1]; applyIncluded(model, s); }
    return s;
  }
  function versionOf(model, state) { return find(model.versions, function (v) { return v.sku === state.version; }); }
  function includesKit(v) { return !!(v && v.includes && v.includes.hose_kit); }
  function applyIncluded(model, s) {
    var v = versionOf(model, s);
    if (includesKit(v) && !s.hose.mode && model.steps.indexOf('hose') > -1) { s.hose = noHose(); s.hose.mode = 'included'; return true; }
    return false;
  }
  /* The kits for a port count, and the pieces the hose step shows. */
  function kitsFor(model, ports) { return model.hose.filter(function (k) { return k.ports === ports; }); }
  function sizesOf(model) { return uniq(model.hose.filter(function (k) { return k.size != null; }).map(function (k) { return k.size; })).sort(function (a, b) { return Number(a) - Number(b); }); }
  function sizeLabel(model, ports, size) {
    var k = find(model.hose, function (x) { return x.ports === ports && x.size === size; }) || find(model.hose, function (x) { return x.size === size; });
    return k && k.size_label ? k.size_label : size + ' in';
  }
  function shortLength(l) { return text(l).split(' (')[0]; }
  function kitOf(model, hose) {
    if (!hose || hose.mode !== 'kit' || !hose.ports) return null;
    var kits = kitsFor(model, hose.ports);
    /* kits with no size (one "standard hose kit" for the unit) resolve on the port count, or on the kit picked */
    var sized = kits.filter(function (k) { return k.size != null; });
    if (!sized.length) return kits.length === 1 ? kits[0] : find(kits, function (k) { return k.sku === hose.pick; });
    if (!hose.size) return null;
    return find(sized, function (k) { return k.size === hose.size && (k.hose || null) === (hose.length || null); });
  }
  /* what a tile says under its label: a price (the page formats it) or "by quote", plus a note from the price list */
  function moneySub(k, note) { return { price: k && k.sold ? k.price : null, quote: !!k && !k.sold, note: note || null }; }
  /* What the hose step offers for the state: ports, sizes, lengths, each with enabled / reason / price. */
  function hoseView(model, state) {
    var v = versionOf(model, state);
    var h = state.hose;
    var ports = [];
    if (includesKit(v)) ports.push({ value: 'included', label: str('included'), sub: str('included_sub'), enabled: true, checked: h.mode === 'included' });
    [1, 2, 3].forEach(function (n) {
      var any = kitsFor(model, n).length > 0;
      var reason = any ? null : str('no_kit_listed', { kind: KIT[n], model: model.name });
      ports.push({ value: String(n), label: PORTS[n], sub: any ? str('kit_word', { Kind: cap(KIT[n]) }) : reason, reason: reason, enabled: any, checked: h.mode === 'kit' && h.ports === n });
    });
    if (model.hoseNone) ports.push({ value: 'own', label: str('own'), sub: str('own_sub'), enabled: true, checked: h.mode === 'own' });
    var sizes = [];
    var lengths = [];
    var picks = [];
    if (h.mode === 'kit' && h.ports) {
      var kits = kitsFor(model, h.ports);
      var sized = kits.filter(function (k) { return k.size != null; });
      if (!sized.length) {
        /* the unit's kits aren't sized by port: list them as they are */
        if (kits.length > 1) picks = kits.map(function (k) { var m = moneySub(k, k.note); return { value: k.sku, label: k.label, price: m.price, quote: m.quote, note: m.note, enabled: true, checked: h.pick === k.sku }; });
      } else {
        var own = uniq(sized.map(function (k) { return k.size; }));
        sizes = sizesOf(model).map(function (s) {
          var std = find(sized, function (k) { return k.size === s && !k.hose; }) || find(sized, function (k) { return k.size === s; });
          var reason = null;
          if (!std) reason = own.length === 1 ? str('kit_only', { Kind: cap(KIT[h.ports]), size: sizeLabel(model, h.ports, own[0]) }) : str('no_kit', { kind: KIT[h.ports] });
          var m = moneySub(std, null);
          return { value: s, label: sizeLabel(model, h.ports, s), enabled: !!std, reason: reason, price: m.price, quote: m.quote, note: null, checked: h.size === s };
        });
        /* hose lengths: offered when the chosen size comes in more than the standard hose */
        var forSize = h.size ? sized.filter(function (k) { return k.size === h.size && k.hose; }) : [];
        if (forSize.length) {
          var all = uniq(sized.filter(function (k) { return k.hose; }).map(function (k) { return k.hose; }));
          var stdKit = find(sized, function (k) { return k.size === h.size && !k.hose; });
          var ms = moneySub(stdKit, null);
          lengths.push({ value: '', label: str('standard_hose'), enabled: !!stdKit, reason: null, price: ms.price, quote: ms.quote, note: null, checked: !h.length });
          all.forEach(function (l) {
            var k = find(sized, function (x) { return x.size === h.size && x.hose === l; });
            var forSizes = uniq(sized.filter(function (x) { return x.hose === l; }).map(function (x) { return sizeLabel(model, h.ports, x.size); }));
            var ml = moneySub(k, k ? k.note : null);
            lengths.push({ value: l, label: l, enabled: !!k, reason: k ? null : str('length_only', { sizes: forSizes.join(' and ') }), price: ml.price, quote: ml.quote, note: ml.note, checked: h.length === l });
          });
        }
      }
    }
    return { ports: ports, sizes: sizes, lengths: lengths, picks: picks, kit: kitOf(model, h), multi: [1, 2, 3].filter(function (n) { return kitsFor(model, n).length > 0; }).length > 1 };
  }
  function stepDone(model, state, step) {
    if (step === 'version') return !!versionOf(model, state);
    if (step === 'hose') return state.hose.mode === 'included' || state.hose.mode === 'own' || !!kitOf(model, state.hose);
    if (step === 'review') return false;
    return !!state.passed[step];
  }
  function stepStatus(model, state, step) {
    if (step === 'review') return 'last';
    if (stepDone(model, state, step)) return 'done';
    return step === 'version' || step === 'hose' ? 'required' : 'optional';
  }
  function nextStep(model, step) { var i = model.steps.indexOf(step); return i > -1 && i < model.steps.length - 1 ? model.steps[i + 1] : null; }
  function firstUndone(model, state) { return find(model.steps, function (s) { return (s === 'version' || s === 'hose') && !stepDone(model, state, s); }); }
  /* A step can open once the required steps before it are done. */
  function canOpen(model, state, step) {
    var i = model.steps.indexOf(step);
    return i > -1 && !find(model.steps.slice(0, i), function (s) { return (s === 'version' || s === 'hose') && !stepDone(model, state, s); });
  }
  /* A build is quoted, not carted, on a quote-only unit and when the chosen version can't be ordered online. */
  function quoteBuild(model, state) { var v = versionOf(model, state); return model.quoteOnly || (!!v && !v.available); }
  /* Why "Add system to cart" can't be used yet ('' when it can). */
  function blocker(model, state) {
    if (model.quoteOnly) return 'quote-only';
    var v = versionOf(model, state);
    if (!v) return str('need_version');
    if (!v.available) return str(model.versions.length > 1 ? 'unavailable' : 'unavailable_unit');
    if (model.steps.indexOf('hose') > -1 && !stepDone(model, state, 'hose')) return str('need_hose');
    return '';
  }

  /* ---------------- actions: each returns { state, live } and never changes its input ---------------- */
  function clone(s) { return JSON.parse(JSON.stringify(s)); }
  function act(model, state, action) {
    var s = clone(state);
    var live = '';
    var same = { state: state, live: '' };
    s.note = null;
    if (action.type === 'version') {
      var v = find(model.versions, function (x) { return x.sku === action.sku; });
      if (!v) return same;
      s.version = v.sku;
      live = str('live_version', { title: v.title });
      if (s.hose.mode === 'included' && !includesKit(v)) {
        /* rule 1: the included kit leaves with the version */
        s.hose = noHose();
        s.note = { step: 'hose', text: /^standard$/i.test(trim(v.title)) ? str('reset_included_std') : str('reset_included', { title: v.title }) };
        live += str('live_included_removed');
      } else if (applyIncluded(model, s)) {
        /* rule 2: a version with a kit and no hose choice yet takes its own kit */
        live += str('live_included_added');
      }
    } else if (action.type === 'ports') {
      if (action.value === 'included') {
        if (!includesKit(versionOf(model, s))) return same;
        s.hose = noHose(); s.hose.mode = 'included';
      } else if (action.value === 'own') {
        if (!model.hoseNone) return same;
        s.hose = noHose(); s.hose.mode = 'own';
      } else {
        var n = Number(action.value);
        var kits = kitsFor(model, n);
        if (!kits.length) return same;
        var prev = s.hose;
        var keep = prev.mode === 'kit';
        s.hose = { mode: 'kit', ports: n, size: keep ? prev.size : null, length: keep ? prev.length : null, pick: null };
        if (s.hose.size && !find(kits, function (k) { return k.size === s.hose.size; })) {
          /* rule 3: no kit for this port count in the chosen size */
          s.note = { step: 'hose', text: str('reset_size', { kind: KIT[n], size: sizeLabel(model, prev.ports, s.hose.size) }) };
          s.hose.size = null; s.hose.length = null;
          live = str('live_size');
        } else if (s.hose.size && s.hose.length && !find(kits, function (k) { return k.size === s.hose.size && k.hose === s.hose.length; })) {
          /* the chosen length doesn't exist for this kit: back to the standard hose, and say so */
          var others = uniq(kits.filter(function (k) { return k.hose === s.hose.length; }).map(function (k) { return sizeLabel(model, n, k.size); }));
          s.note = { step: 'hose', text: others.length ? str('reset_length', { length: shortLength(s.hose.length), sizes: others.join(' and ') }) : str('reset_length_kit', { kind: KIT[n] }) };
          s.hose.length = null;
          live = str('live_length');
        }
      }
    } else if (action.type === 'size') {
      if (s.hose.mode !== 'kit') return same;
      var ks = kitsFor(model, s.hose.ports);
      if (!find(ks, function (k) { return k.size === action.value; })) return same;
      s.hose.size = action.value;
      if (s.hose.length && !find(ks, function (k) { return k.size === action.value && k.hose === s.hose.length; })) {
        /* rule 4: the chosen length doesn't come for this size */
        var sizes = uniq(ks.filter(function (k) { return k.hose === s.hose.length; }).map(function (k) { return sizeLabel(model, s.hose.ports, k.size); }));
        s.note = { step: 'hose', text: str('reset_length', { length: shortLength(s.hose.length), sizes: sizes.join(' and ') }) };
        s.hose.length = null;
        live = str('live_length');
      }
    } else if (action.type === 'length') {
      if (s.hose.mode !== 'kit' || !s.hose.size) return same;
      var want = action.value || null;
      if (!find(kitsFor(model, s.hose.ports), function (k) { return k.size === s.hose.size && (k.hose || null) === want; })) return same;
      s.hose.length = want;
    } else if (action.type === 'pick') {
      if (s.hose.mode !== 'kit' || !find(kitsFor(model, s.hose.ports), function (k) { return k.sku === action.value; })) return same;
      s.hose.pick = action.value;
    } else if (action.type === 'capture') {
      if (action.value !== 'default' && !find(model.capture, function (c) { return c.sku === action.value; })) return same;
      s.capture = action.value;
    } else if (action.type === 'addon') {
      if (!find(model.addons, function (a) { return a.sku === action.sku && !a.group; })) return same;
      if (action.on) s.addons[action.sku] = true; else delete s.addons[action.sku];
    } else if (action.type === 'ilf') {
      if (action.value && !find(model.addons, function (a) { return a.sku === action.value && a.group === 'ilf'; })) return same;
      s.ilf = action.value || null;
    } else if (action.type === 'spare') {
      if (!model.spare.length) return same;
      s.spare = !!action.on;
    } else if (action.type === 'open') {
      if (model.steps.indexOf(action.step) < 0 || !canOpen(model, s, action.step)) return same;
      s.open = action.step;
      s.note = state.note && state.note.step === action.step ? state.note : null;
    } else if (action.type === 'continue') {
      var from = action.step;
      var next = nextStep(model, from);
      if (!next || ((from === 'version' || from === 'hose') && !stepDone(model, s, from))) return same;
      s.passed[from] = true;
      s.open = next;
      s.note = state.note && state.note.step === next ? state.note : null;
    } else return same;
    return { state: s, live: live };
  }

  /* ---------------- summary ---------------- */
  /* Every chosen line in step order. cart = it goes in the cart (sold online, and the unit isn't quote-only). */
  function lines(model, state) {
    var out = [];
    var v = versionOf(model, state);
    if (!v) return out;
    var single = model.versions.length === 1;
    var p = model.cfg.power;
    var supply = p && p.value ? p.value + (p.sub ? ', ' + text(p.sub).split(',')[0] : '') : '';
    var quoted = quoteBuild(model, state);
    out.push({ step: 'version', kind: 'unit', name: model.name, meta: single ? (supply ? supply + ' · ' : '') + v.sku : str('meta_version', { title: v.title, sku: v.sku }), sku: v.sku, variant_id: v.id, price: v.price, compare_at: v.compare_at, cart: !quoted, priced: true, image: null });
    var push = function (o, step, metaKey, name) { out.push({ step: step, kind: o.kind, name: name || o.label, meta: str(metaKey, { sku: o.sku }), sku: o.sku, variant_id: o.variant_id, price: o.sold ? o.price : null, compare_at: o.sold ? o.compare_at : null, cart: !quoted && o.sold, priced: o.sold, image: o.image }); };
    var kit = kitOf(model, state.hose);
    if (kit) push(kit, 'hose', 'meta_hose');
    if (state.capture && state.capture !== 'default') { var c = find(model.capture, function (x) { return x.sku === state.capture; }); if (c) push(c, 'capture', 'meta_capture'); }
    model.addons.forEach(function (a) { if ((!a.group && state.addons[a.sku]) || (a.group === 'ilf' && state.ilf === a.sku)) push(a, 'addons', 'meta_addon'); });
    if (state.spare) model.spare.forEach(function (f) { push(f, 'addons', 'meta_spare', cap(f.label)); });
    return out;
  }
  function summary(model, state) {
    var all = lines(model, state);
    /* a quoted build: every priced line is "on your quote"; otherwise the cart lines */
    var quoted = quoteBuild(model, state);
    var main = all.filter(function (l) { return quoted ? l.priced : l.cart; });
    var quote = all.filter(function (l) { return quoted ? !l.priced : !l.cart; });
    var total = main.reduce(function (t, l) { return t + (l.price || 0); }, 0);
    var list = main.reduce(function (t, l) { return t + (l.compare_at != null ? l.compare_at : l.price || 0); }, 0);
    return {
      lines: all, main: main, cart: quoted ? [] : main, quote: quote, quoted: quoted,
      count: main.length, total: total, list: list > total ? list : null,
      included: state.hose.mode === 'included', own: state.hose.mode === 'own',
      blocker: blocker(model, state)
    };
  }
  function cartItems(model, state) { return summary(model, state).cart.map(function (l) { return { id: l.variant_id, quantity: 1 }; }); }
  /* /pages/bofa-quote-request?model=<slug>&build=SKU:1,SKU:1 — every chosen line, cart and quote alike, in step order. */
  function quoteHref(model, state) {
    var cfg = model.cfg;
    var all = lines(model, state).slice(0, MAX_QUOTE_LINES);
    var q = [];
    if (cfg.slug && all.length && !model.plain) {
      q.push('model=' + encodeURIComponent(cfg.slug));
      q.push('build=' + encodeURIComponent(all.map(function (l) { return l.sku + ':1'; }).join(',')));
    } else {
      /* nothing chosen yet, the plain buy box (one product, any quantity), or a unit the finder has no model for:
         the product, and its part number when a version is chosen */
      q.push('product=' + encodeURIComponent(cfg.title));
      var v = versionOf(model, state);
      if (v) q.push('part=' + encodeURIComponent(v.sku));
    }
    return cfg.quote + '?' + q.join('&');
  }
  function twinPacks(model) { return model.spare.length > 0 && model.spare.every(function (f) { return /twin pack/i.test(f.label); }); }
  function spareTitle(model) { return str(model.spare.length === 1 ? 'spare_title_one' : twinPacks(model) ? 'spare_title_twin' : 'spare_title'); }
  /* "A", "A and B", "A, B and C" */
  function listOf(items) { return items.length < 3 ? items.join(' and ') : items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1]; }
  /* The collapsed row's text for a step. */
  function choiceText(model, state, step) {
    var v = versionOf(model, state);
    if (step === 'version') return v ? (model.versions.length === 1 ? versionLine(model, v) : v.title + ' · ' + v.sku) : '';
    if (step === 'hose') {
      if (state.hose.mode === 'included') return str('included_line');
      if (state.hose.mode === 'own') return str('own');
      var k = kitOf(model, state.hose);
      return k ? k.label + ' · ' + k.sku : '';
    }
    if (step === 'capture') {
      if (!state.capture || state.capture === 'default') return state.passed.capture || state.capture ? text(model.captureDefault) : '';
      var c = find(model.capture, function (x) { return x.sku === state.capture; });
      return c ? c.label : '';
    }
    if (step === 'addons') {
      var names = model.addons.filter(function (a) { return (!a.group && state.addons[a.sku]) || (a.group === 'ilf' && state.ilf === a.sku); }).map(function (a) { return a.label; });
      if (state.spare) { var sp = spareTitle(model); names.push(names.length ? lowerFirst(sp) : sp); }
      return names.length ? names.join(', ') : (state.passed.addons ? str('review_none') : '');
    }
    return '';
  }
  /* "One version: 208 V, 3-phase · remote signals · exhaust spigot" */
  function versionLine(model, v) {
    var bits = [];
    var p = model.cfg.power;
    if (p && p.value) bits.push(p.value + (p.sub ? ', ' + text(p.sub).split(',')[0] : ''));
    var inc = v.includes || {};
    if (inc.stop_start || inc.system_ok) bits.push(str('signals'));
    if (inc.compressor) bits.push(str('compressor'));
    if (inc.hose_kit) bits.push(str('hose_kit'));
    if (inc.spigot) bits.push(str('spigot'));
    return str('one_version') + (bits.length ? ': ' + bits.join(' · ') : '');
  }
  function spareDesc(model) {
    var labels = model.spare.map(function (f) { return f.label; });
    var plain = !labels.some(function (l) { return /pack|,/.test(l); });
    if (labels.length === 1 && plain) return str('spare_desc_one', { a: labels[0] });
    if (labels.length === 2 && plain) return str('spare_desc_two', { a: labels[0], b: labels[1] });
    return str(labels.length === 1 ? 'spare_desc_one_plain' : 'spare_desc', { list: listOf(labels), a: labels[0] });
  }
  function filtersNote(model) {
    var f = model.filtersByQuote.map(function (x) { return lowerFirst(x.label); });
    if (!f.length) return '';
    if (f.length === 1) return str('filters_note_one', { a: f[0] });
    if (f.length === 2) return str('filters_note_two', { a: f[0], b: f[1] });
    return str('filters_note_many');
  }

  /* ---------------- persistence: the build per product handle, for Back / refresh ---------------- */
  function saveState(storage, key, state) { try { if (storage && key) storage.setItem(key, JSON.stringify({ v: 1, s: state })); } catch (e) { /* storage blocked */ } }
  /* A stored build is replayed through the same actions, so anything the store no longer offers simply doesn't apply. */
  function loadState(storage, key, model) {
    var s = emptyState(model);
    var raw = null;
    try { raw = storage && key ? JSON.parse(storage.getItem(key) || 'null') : null; } catch (e) { raw = null; }
    var o = raw && raw.v === 1 && raw.s && typeof raw.s === 'object' ? raw.s : null;
    if (!o) return s;
    var run = function (a) { s = act(model, s, a).state; };
    if (typeof o.version === 'string') run({ type: 'version', sku: o.version });
    var h = o.hose && typeof o.hose === 'object' ? o.hose : {};
    if (h.mode === 'included') run({ type: 'ports', value: 'included' });
    else if (h.mode === 'own') run({ type: 'ports', value: 'own' });
    else if (h.mode === 'kit' && h.ports) {
      run({ type: 'ports', value: String(h.ports) });
      if (h.size != null) run({ type: 'size', value: String(h.size) });
      if (h.length) run({ type: 'length', value: String(h.length) });
      if (h.pick) run({ type: 'pick', value: String(h.pick) });
    }
    if (typeof o.capture === 'string') run({ type: 'capture', value: o.capture });
    if (o.addons && typeof o.addons === 'object') Object.keys(o.addons).forEach(function (k) { run({ type: 'addon', sku: k, on: true }); });
    if (typeof o.ilf === 'string') run({ type: 'ilf', value: o.ilf });
    if (o.spare) run({ type: 'spare', on: true });
    if (o.passed && typeof o.passed === 'object') model.steps.forEach(function (st) { if (o.passed[st] && st !== 'review' && (st === 'capture' || st === 'addons' || stepDone(model, s, st))) s.passed[st] = true; });
    if (model.steps.indexOf(o.open) > -1) s.open = o.open;
    /* never reopen on a step that sits behind an unfinished required one */
    if (!canOpen(model, s, s.open)) s.open = firstUndone(model, s) || s.open;
    s.note = null;
    return s;
  }

  /* The build a page load starts with: the stored one; nothing chosen when there is none. A link that names a version
     (?variant=…, as product ads and the store's own variant links do) is the latest word on the version: it is applied
     to the stored build through the same rules as a click (a reset note stays, so the customer reads why). The address
     follows the version chosen (syncUrl), so a refresh names the customer's own last choice.
     keepStored: the customer came here with Back or Forward. An old address may name a version they have since changed:
     the stored build is the newer word, so its version stays. */
  function initialState(storage, key, model, keepStored) {
    var s = loadState(storage, key, model);
    var sel = model.cfg ? model.cfg.selected : null;
    if (sel && s.version !== sel && !(keepStored && s.version)) {
      s = act(model, s, { type: 'version', sku: sel }).state;
      /* never left open on a step that sits behind an unfinished required one */
      if (!canOpen(model, s, s.open)) s.open = firstUndone(model, s) || s.open;
    }
    return s;
  }

  /* ---------------- DOM ---------------- */
  function qs(root, sel) { return root ? root.querySelector(sel) : null; }
  function qsa(root, sel) { return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : []; }
  function readIsland(root, sel) { var n = qs(root, sel); if (!n) return null; try { return JSON.parse(n.textContent || 'null'); } catch (e) { return null; } }
  function show(n, on) { if (n) n.hidden = !on; }
  function setText(n, value) { if (n) n.textContent = text(value); }
  function sessionStore() { try { return window.sessionStorage; } catch (e) { return null; } }
  /* el('div', { class: 'x', 'data-y': '' }, [children | text]) */
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'text') n.textContent = text(v);
      else if (k === 'hidden' || k === 'disabled' || k === 'checked') { if (v) n[k] = true; }
      else n.setAttribute(k, v === true ? '' : String(v));
    });
    (kids || []).forEach(function (c) { if (c == null) return; n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function clear(n) { while (n && n.firstChild) n.removeChild(n.firstChild); }
  /* A product photo URL from the island: only a same-site or https URL is ever put in src. */
  function safeSrc(u) { var s = trim(u); return /^(https:)?\/\/[^\s]+$|^\/(?!\/)[^\s]*$/i.test(s) ? s : ''; }

  var cartChain = null;
  function cartUrl() {
    var r = '/';
    try { if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) r = String(window.Shopify.routes.root); } catch (e) { r = '/'; }
    return (r.charAt(r.length - 1) === '/' ? r : r + '/') + 'cart/add.js';
  }
  /* One POST /cart/add.js with items[]; 15 s timeout; requests never overlap. Resolves { key }; rejects { message }. */
  function cartAdd(items) {
    var run = function () {
      return new Promise(function (resolve, reject) {
        if (typeof window.fetch !== 'function') { reject({ message: '' }); return; }
        var done = false;
        var ctrl = null;
        try { ctrl = typeof window.AbortController === 'function' ? new window.AbortController() : null; } catch (e) { ctrl = null; }
        var timer = setTimeout(function () {
          if (done) return;
          done = true;
          try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
          reject({ message: '' });
        }, CART_TIMEOUT);
        var opts = { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: items }) };
        if (ctrl) opts.signal = ctrl.signal;
        window.fetch(cartUrl(), opts).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, status: r.status, data: data || {} }; });
        }).then(function (res) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          if (!res.ok) { reject({ message: res.status === 422 ? trim(res.data.description || res.data.message || '').replace(/\.+$/, '') : '' }); return; }
          var list = Array.isArray(res.data.items) ? res.data.items : [res.data];
          var lead = find(list, function (it) { return it && String(it.variant_id || it.id) === String(items[0].id); }) || list[0];
          resolve({ key: lead && lead.key ? String(lead.key) : null });
        }, function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          reject({ message: '' });
        });
      });
    };
    var prev = cartChain || Promise.resolve();
    var p = prev.then(run, run);
    cartChain = p.then(function () { return null; }, function () { return null; });
    return p;
  }
  function openCart(key) {
    if (typeof window.openFlyoutCart === 'function') {
      try { window.openFlyoutCart({ addedKey: key }); return; } catch (e) { /* fall back */ }
    }
    var trig = document.querySelector('.flyout-trigger');
    if (trig) trig.click();
  }

  /* The theme's Webflow runtime (assets/h1-custom.js) takes every same-page #id link through a jQuery handler on document
     ("click.wf-scroll") and scrolls the target under the pinned header. Remove that one handler (as the collection page does). */
  function disableWebflowAnchorScroll() {
    var off = function () { try { if (window.jQuery) window.jQuery(document).off('click.wf-scroll'); } catch (e) { /* no jQuery */ } };
    off();
    try { if (window.Webflow && typeof window.Webflow.push === 'function') window.Webflow.push(off); } catch (e) { /* no Webflow */ }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(off, 0); });
    window.addEventListener('load', off);
  }
  /* --bup-header-h: the pinned header's height, so the sticky summary and the scroll targets sit below it */
  function measureHeader(root, selector) {
    var last = -1;
    var read = function () {
      var h = 0;
      var node = null;
      try { node = document.querySelector(selector || '.abh-section'); } catch (e) { node = null; }
      for (var n = node; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        var pos = '';
        try { pos = window.getComputedStyle(n).position; } catch (e) { pos = ''; }
        if (pos === 'sticky' || pos === 'fixed') { h = Math.max(0, Math.round(n.getBoundingClientRect().height)); break; }
      }
      if (h !== last) { last = h; root.style.setProperty('--bup-header-h', h + 'px'); }
    };
    read();
    window.addEventListener('resize', read);
    window.addEventListener('load', read);
    if (typeof window.ResizeObserver === 'function') { try { var hd = document.querySelector(selector || '.abh-section'); if (hd) new window.ResizeObserver(read).observe(hd); } catch (e) { /* ignore */ } }
  }

  /* Below 1024 px the photos sit between the heading and the buy box (CSS order). The node moves there too, so the Tab
     order is the order on screen; from 1024 px up it is back at the top of the right column. */
  function placeVisual(root) {
    var visual = qs(root, '.bup-visual');
    var aside = qs(root, '.bup-aside');
    var buy = qs(root, '.bup-buy');
    if (!visual || !aside || !buy || typeof window.matchMedia !== 'function') return;
    var mq = window.matchMedia('(max-width: 1023px)');
    var place = function () {
      /* a control inside the photos keeps the focus through the move */
      var had = visual.contains(document.activeElement) ? document.activeElement : null;
      if (mq.matches) {
        if (visual.nextElementSibling !== buy) buy.parentNode.insertBefore(visual, buy);
        /* what is left in the right column is not shown at this width: no empty landmark */
        aside.setAttribute('aria-hidden', 'true');
      } else {
        if (visual.parentNode !== aside) aside.insertBefore(visual, aside.firstChild);
        aside.removeAttribute('aria-hidden');
      }
      if (had && document.activeElement !== had && had.focus) had.focus({ preventScroll: true });
    };
    place();
    if (mq.addEventListener) mq.addEventListener('change', place); else if (mq.addListener) mq.addListener(place);
  }
  /* 'back_forward' when the customer came to this page with Back or Forward ('' when the browser doesn't say). */
  function navType() {
    try {
      var n = window.performance && window.performance.getEntriesByType ? window.performance.getEntriesByType('navigation')[0] : null;
      return n && n.type ? String(n.type) : '';
    } catch (e) { return ''; }
  }
  /* The address names the version chosen (?variant=…), as product pages do: a refresh, a shared link and the first paint
     then show the same version. Nothing is added to the history. A one-version unit has nothing to name. */
  function syncUrl(model, v) {
    try {
      if (!v || model.versions.length < 2 || !window.history || typeof window.history.replaceState !== 'function' || typeof window.URL !== 'function') return;
      var u = new window.URL(window.location.href);
      if (u.searchParams.get('variant') === String(v.id)) return;
      u.searchParams.set('variant', String(v.id));
      window.history.replaceState(window.history.state, '', u.toString());
    } catch (e) { /* the address stays as it is */ }
  }

  function mount(root) {
    if (!root || root.__bup) return;
    root.__bup = true;
    var cfg = readIsland(root, '[data-bup-config]');
    if (!cfg) return;
    var model = buildModel(cfg, readIsland(root, '[data-bup-options]'), readIsland(root, '[data-bup-products]'));
    var money = function (cents) { return formatMoney(cents, cfg.money_format); };
    var listRef = function (cents) { return str('list_ref', { label: cfg.list_label, price: money(cents) }); };
    var live = qs(root, '[data-bup-live]');
    var announce = function (msg) { if (live && msg) { live.textContent = ''; window.setTimeout(function () { live.textContent = msg; }, 30); } };

    disableWebflowAnchorScroll();
    measureHeader(root, cfg.header_selector);
    placeVisual(root);
    /* in-page links (test conditions, Filters for the …): land below the pinned header, focus the target */
    root.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!a || !root.contains(a) || a.hasAttribute('data-bup-step-link')) return;
      var id = a.getAttribute('href').slice(1);
      var target = id ? document.getElementById(id) : null;
      if (!target || !root.contains(target)) return;
      e.preventDefault();
      target.scrollIntoView({ block: 'start' });
      if (target.focus) target.focus({ preventScroll: true });
    });
    /* photos: a thumbnail swaps the main photo */
    var photo = qs(root, '[data-bup-photo]');
    qsa(root, '[data-bup-thumb]').forEach(function (t) {
      t.addEventListener('click', function (e) {
        var src = safeSrc(t.getAttribute('data-bup-thumb'));
        if (!photo || !src) return;
        e.preventDefault();
        photo.removeAttribute('srcset');
        photo.src = src;
        var im = qs(t, 'img');
        if (im) photo.alt = im.alt || '';
        qsa(root, '[data-bup-thumb]').forEach(function (o) { if (o === t) o.setAttribute('aria-current', 'true'); else o.removeAttribute('aria-current'); });
      });
    });

    /* ---------- buy box: price row, SKU chip ---------- */
    function renderBuy(v) {
      var low = model.versions.reduce(function (m, x) { return m == null || x.price < m.price ? x : m; }, null);
      var shown = v || low;
      if (!shown) return;
      show(qs(root, '[data-bup-from]'), !v && model.versions.some(function (x) { return x.price !== low.price; }));
      setText(qs(root, '[data-bup-price]'), money(shown.price));
      var list = qs(root, '[data-bup-list]');
      show(list, shown.compare_at != null);
      if (shown.compare_at != null) setText(list, listRef(shown.compare_at));
      var vt = qs(root, '[data-bup-vtitle]');
      show(vt, !!v && model.versions.length > 1);
      setText(vt, v && model.versions.length > 1 ? '· ' + v.title : '');
      var chip = qs(root, '[data-bup-sku]');
      show(chip, !!v);
      if (v && chip) setText(qs(chip, '[data-slot="sku"]'), v.sku);
    }

    /* ================= the plain buy box: nothing to build with ================= */
    if (model.plain) {
      root.setAttribute('data-bup-js', 'on');
      var box = qs(root, '[data-bup-plain]');
      var radios = qsa(box, 'input[type="radio"][name="id"]');
      var quoteLink = qs(box, '[data-bup-plain-quote]');
      var form = qs(box, '[data-bup-form]');
      var addBtn = qs(box, '[data-bup-plain-add]');
      var pStatus = qs(box, '[data-bup-plain-status]');
      var pError = qs(box, '[data-bup-plain-error]');
      var pBusy = false;
      var pstate = emptyState(model);
      /* With several versions none is checked until the customer checks one (or the link names one: the section checks
         it). The form's own "required" stops an Add with no version. */
      var sync = function (ev) {
        var r = find(radios, function (x) { return x.checked; });
        if (r) pstate.version = r.getAttribute('data-sku');
        var v = versionOf(model, pstate);
        renderBuy(v);
        if (quoteLink) quoteLink.setAttribute('href', quoteHref(model, pstate));
        /* a version (or a unit) that can't be ordered online: Add is off and the line under it says why */
        if (addBtn && !model.quoteOnly) {
          var off = v ? !v.available : !model.versions.some(function (x) { return x.available; });
          var why = off ? str(v && model.versions.length > 1 ? 'unavailable' : 'unavailable_unit') : '';
          addBtn.disabled = off;
          setText(pStatus, why);
          /* on a change the reason is also said through the page's live region (the line is not rendered while empty) */
          if (ev && why) announce(why);
        }
        if (ev) syncUrl(model, v);
        show(pError, false);
      };
      radios.forEach(function (r) { r.addEventListener('change', sync); });
      sync();
      /* Back / Forward: the browser puts the customer's own checked radio back after this script has run */
      window.addEventListener('pageshow', function () { sync(); syncUrl(model, versionOf(model, pstate)); });
      if (form && addBtn && !model.quoteOnly) form.addEventListener('submit', function (e) {
        var v = versionOf(model, pstate);
        if (!v || typeof window.fetch !== 'function') return; /* the form posts as it is */
        e.preventDefault();
        if (pBusy) return;
        pBusy = true;
        show(pError, false);
        var label = qs(addBtn, '[data-slot="label"]');
        addBtn.setAttribute('aria-busy', 'true');
        setText(label, str('adding'));
        var done = function () { pBusy = false; addBtn.removeAttribute('aria-busy'); setText(label, str('add_plain')); };
        cartAdd([{ id: v.id, quantity: 1 }]).then(function (res) {
          done();
          /* shown in the status line, said once through the page's live region */
          var added = str('added_one', { model: model.name });
          setText(pStatus, added);
          announce(added);
          openCart(res.key);
        }, function (err) { done(); setText(pError, (err && err.message) || str('add_failed')); show(pError, true); });
      });
      return;
    }

    /* ================= the builder ================= */
    var store = sessionStore();
    var state = initialState(store, cfg.store_key, model, navType() === 'back_forward');
    saveState(store, cfg.store_key, state);
    /* the address names the version the page shows */
    syncUrl(model, versionOf(model, state));
    var busy = false;
    var addError = ''; /* the last failed add, shown in every copy of the actions until the build changes or the add works */
    root.setAttribute('data-bup-js', 'on');

    function skus() { return lines(model, state).map(function (l) { return l.sku; }).join(','); }
    function totalsLive() {
      var sum = summary(model, state);
      if (!sum.lines.length) return '';
      var msg = str(sum.quoted ? 'live_total_quote' : 'live_total', { n: sum.count, total: money(sum.total) });
      if (sum.quote.length) msg += str('live_by_quote', { n: sum.quote.length });
      return msg;
    }
    function dispatch(action, focusId) {
      var before = skus();
      var r = act(model, state, action);
      if (r.state === state) return;
      state = r.state;
      /* the build changed: the last add's result (an error, or "Added to cart: …") no longer describes it */
      if (skus() !== before) { addError = ''; setText(status, ''); }
      saveState(store, cfg.store_key, state);
      if (action.type === 'version') syncUrl(model, versionOf(model, state));
      render();
      var msg = r.live;
      if (skus() !== before) msg = (msg ? msg + ' ' : '') + totalsLive();
      if (msg) announce(msg);
      /* focus stays on the control just used (its id survives the re-render); a step that opens takes the focus */
      if (focusId) { var f = document.getElementById(focusId); if (f && document.activeElement !== f) f.focus({ preventScroll: action.type !== 'open' && action.type !== 'continue' }); }
    }

    /* ---------- pieces ---------- */
    function thumb(src) { var s = safeSrc(src); return el('span', { class: 'bup-opt__thumb', 'aria-hidden': 'true' }, s ? [el('img', { src: s, alt: '', loading: 'lazy', width: '88', height: '72' })] : []); }
    /* a tile's small line: "With cuffs · by quote", "$201.40", "By quote", or the reason it can't be chosen */
    function tileSub(o) {
      if (o.reason) return o.reason;
      var bits = [];
      if (o.note) bits.push(o.note);
      if (o.price != null) bits.push(money(o.price));
      else if (o.quote) bits.push(bits.length ? str('by_quote_lc') : str('by_quote'));
      return bits.join(' · ');
    }
    /* A real radio or checkbox in a label. o: { id, name, type, value, checked, enabled, label, sub, desc, sku, price, list, quote, reason, compact, image } */
    function choice(o, onChange) {
      var input = el('input', { type: o.type || 'radio', class: 'bup-opt__input', id: o.id, name: o.name, value: o.value, checked: !!o.checked, disabled: o.enabled === false });
      if (o.reason) input.setAttribute('aria-describedby', o.id + '-why');
      input.addEventListener('change', function () { onChange(input); });
      var body = el('span', { class: 'bup-opt__body' }, [
        el('span', { class: 'bup-opt__label', text: o.label }),
        o.desc ? el('span', { class: 'bup-opt__desc', text: o.desc }) : null,
        o.sku ? el('span', { class: 'bup-pn', text: o.sku }) : null,
        o.sub ? el('span', { class: 'bup-opt__sub', id: o.reason ? o.id + '-why' : null, text: o.sub }) : null
      ]);
      var kids = [input, el('span', { class: 'bup-opt__mark', 'aria-hidden': 'true' })];
      if (o.image !== undefined) kids.push(thumb(o.image));
      kids.push(body);
      if (!o.compact && (o.price != null || o.quote)) {
        var wrap = el('span', { class: 'bup-opt__money' });
        if (o.price != null) {
          wrap.appendChild(el('span', { class: 'bup-opt__price', text: money(o.price) }));
          if (o.list != null) wrap.appendChild(el('span', { class: 'bup-opt__list', text: listRef(o.list) }));
        } else wrap.appendChild(el('span', { class: 'bup-pill', text: o.quote }));
        kids.push(wrap);
      }
      return el('label', { class: 'bup-opt' + (o.compact ? ' bup-opt--compact' : '') + (o.image !== undefined ? ' bup-opt--thumb' : ''), 'data-checked': o.checked ? 'true' : null, 'data-disabled': o.enabled === false ? 'true' : null, for: o.id }, kids);
    }
    function fieldset(legend, hint, kids, mod, srOnly) {
      var fs = el('fieldset', { class: 'bup-fs bup-fs--' + mod }, [el('legend', { class: srOnly ? 'bup-sr' : 'bup-fs__legend', text: legend })]);
      if (hint) fs.appendChild(el('p', { class: 'bup-fs__hint', text: hint }));
      fs.appendChild(el('div', { class: 'bup-fs__opts' }, kids));
      return fs;
    }
    function contactLink() { return el('a', { class: 'bup-link', href: cfg.contact, text: str('contact') }); }
    function stepId(step) { return 'bup-step-' + step + '-' + cfg.sid; }

    /* ---------- tracker ---------- */
    var stepsBox = qs(root, '[data-bup-steps]');
    var tracker = qs(root, '[data-bup-tracker]');
    var stepNow = qs(root, '[data-bup-stepnow]');
    function renderTracker() {
      if (!tracker) return;
      clear(tracker);
      model.steps.forEach(function (step, i) {
        var status = stepStatus(model, state, step);
        var a = el('a', { class: 'bup-track__link', href: '#' + stepId(step), 'data-bup-step-link': step, 'aria-current': state.open === step ? 'step' : null, 'data-status': status }, [
          el('span', { class: 'bup-track__num', 'aria-hidden': 'true', text: status === 'done' ? '✓' : String(i + 1) }),
          el('span', { class: 'bup-track__text' }, [el('span', { class: 'bup-track__name', text: STEP_NAME[step] }), el('span', { class: 'bup-track__status', text: str(status) })])
        ]);
        a.addEventListener('click', function (e) {
          e.preventDefault();
          /* a step behind an unfinished required one opens that required step instead */
          var target = canOpen(model, state, step) ? step : (firstUndone(model, state) || step);
          if (state.open === target) { var cur = document.getElementById(stepId(target)); if (cur) { cur.scrollIntoView({ block: 'nearest' }); cur.focus({ preventScroll: true }); } return; }
          dispatch({ type: 'open', step: target }, stepId(target));
        });
        tracker.appendChild(el('li', { class: 'bup-track__item' }, [a]));
      });
      setText(stepNow, str('step_now', { i: model.steps.indexOf(state.open) + 1, n: model.steps.length, name: STEP_NAME[state.open] }));
    }

    /* ---------- steps ---------- */
    function stepShell(step, i) {
      var open = state.open === step;
      var status = stepStatus(model, state, step);
      var sec = el('section', { class: 'bup-step', id: stepId(step), tabindex: '-1', 'data-step': step, 'data-open': open ? 'true' : 'false', 'data-status': status, 'aria-labelledby': 'bup-step-h-' + step + '-' + cfg.sid });
      var head = el('div', { class: 'bup-step__head' }, [
        el('span', { class: 'bup-step__num', 'aria-hidden': 'true', text: status === 'done' && !open ? '✓' : String(i + 1) }),
        el('h3', { class: 'bup-step__h', id: 'bup-step-h-' + step + '-' + cfg.sid, text: open ? STEP_NAME[step] : (i + 1) + ' · ' + STEP_NAME[step] }),
        step === 'review' && !open ? null : el('span', { class: 'bup-step__status', 'data-status': status, text: str(status) })
      ]);
      if (!open) {
        var choiceTxt = step === 'review' ? str(quoteBuild(model, state) ? 'review_lead_quote' : 'review_lead') : choiceText(model, state, step);
        head.appendChild(el('span', { class: 'bup-step__choice', text: choiceTxt || str('not_chosen') }));
        /* a single-version unit has nothing to change in step 1 */
        if (!(step === 'version' && model.versions.length === 1)) {
          var btn = el('button', { type: 'button', class: 'bup-step__change', id: 'bup-change-' + step + '-' + cfg.sid, text: step === 'review' ? STEP_NAME.review : str('change'), 'aria-label': step === 'review' ? 'Open the review' : 'Change ' + STEP_NAME[step].toLowerCase(), disabled: !canOpen(model, state, step) });
          btn.addEventListener('click', function () { dispatch({ type: 'open', step: step }, stepId(step)); });
          head.appendChild(btn);
        }
      }
      sec.appendChild(head);
      /* a reset note stays with its step, open or not */
      if (!open && state.note && state.note.step === step) sec.appendChild(el('p', { class: 'bup-note bup-note--closed', 'data-bup-note': '', text: state.note.text }));
      return sec;
    }
    function continueBtn(step) {
      var next = nextStep(model, step);
      var b = el('button', { type: 'button', class: 'bup-btn bup-btn--outline bup-step__next', id: 'bup-next-' + step + '-' + cfg.sid, text: str('continue_to', { step: STEP_NAME[next].toLowerCase() }), disabled: (step === 'version' || step === 'hose') && !stepDone(model, state, step) });
      b.addEventListener('click', function () { dispatch({ type: 'continue', step: step }, stepId(next)); });
      return b;
    }

    function renderVersion(body) {
      var name = 'bup-version-' + cfg.sid;
      body.appendChild(fieldset(STEP_NAME.version, null, model.versions.map(function (v, i) {
        return choice({ id: name + '-' + i, name: name, value: v.sku, checked: state.version === v.sku, label: v.title, desc: v.desc, sku: v.sku, price: v.price, list: v.compare_at }, function (input) { dispatch({ type: 'version', sku: v.sku }, input.id); });
      }), 'stack', true));
      body.appendChild(el('div', { class: 'bup-step__foot' }, [continueBtn('version')]));
    }
    function renderHose(body) {
      var view = hoseView(model, state);
      var base = 'bup-hose-' + cfg.sid;
      if (state.note && state.note.step === 'hose') body.appendChild(el('p', { class: 'bup-note', 'data-bup-note': '', text: state.note.text }));
      var words = { inlet: cfg.inlet, machine: cfg.machine || 'machine' };
      body.appendChild(el('p', { class: 'bup-step__lead', text: str(cfg.inlet ? 'hose_lead' : 'hose_lead_plain', words) + (view.multi ? str('hose_lead_pick', words) : '') }));
      body.appendChild(fieldset(str('ports_legend', words), null, view.ports.map(function (p, i) {
        return choice({ id: base + '-ports-' + i, name: base + '-ports', value: p.value, checked: p.checked, enabled: p.enabled, label: p.label, sub: p.sub, reason: p.reason, compact: true }, function (input) { dispatch({ type: 'ports', value: p.value }, input.id); });
      }), 'row'));
      if (view.sizes.length) body.appendChild(fieldset(str('size_legend', words), str('size_hint', words), view.sizes.map(function (s, i) {
        return choice({ id: base + '-size-' + i, name: base + '-size', value: s.value, checked: s.checked, enabled: s.enabled, label: s.label, sub: tileSub(s), reason: s.reason, compact: true }, function (input) { dispatch({ type: 'size', value: s.value }, input.id); });
      }), 'row'));
      if (view.picks.length) body.appendChild(fieldset(STEP_NAME.hose, null, view.picks.map(function (p, i) {
        return choice({ id: base + '-pick-' + i, name: base + '-pick', value: p.value, checked: p.checked, label: p.label, sub: tileSub(p), compact: true }, function (input) { dispatch({ type: 'pick', value: p.value }, input.id); });
      }), 'stack', true));
      if (view.lengths.length) body.appendChild(fieldset(str('length_legend'), null, view.lengths.map(function (l, i) {
        return choice({ id: base + '-len-' + i, name: base + '-len', value: l.value, checked: l.checked, enabled: l.enabled, label: l.label, sub: tileSub(l), reason: l.reason, compact: true }, function (input) { dispatch({ type: 'length', value: l.value }, input.id); });
      }), 'row'));
      var foot = el('div', { class: 'bup-step__foot' });
      if (view.kit) {
        var k = view.kit;
        foot.appendChild(el('p', { class: 'bup-result', 'data-bup-result': k.sku }, [
          el('span', { class: 'bup-result__name', text: k.label }), el('span', { class: 'bup-pn', text: k.sku }),
          k.sold ? el('span', { class: 'bup-result__price', text: money(k.price) }) : el('span', { class: 'bup-pill', text: str('available_by_quote') }),
          k.sold && (model.quoteOnly || k.compare_at != null) ? el('span', { class: 'bup-result__list', text: model.quoteOnly ? str('on_quote') : listRef(k.compare_at) }) : null
        ]));
      }
      foot.appendChild(el('p', { class: 'bup-step__ask' }, [str(view.multi ? 'not_sure' : 'not_sure_kit') + ' ', contactLink()]));
      foot.appendChild(continueBtn('hose'));
      body.appendChild(foot);
    }
    function renderCapture(body) {
      var name = 'bup-capture-' + cfg.sid;
      var cur = state.capture || 'default';
      var withImg = model.capture.some(function (c) { return !!c.image; });
      var kids = [choice({ id: name + '-0', name: name, value: 'default', checked: cur === 'default', label: model.captureDefault, sub: str('capture_default_sub'), image: withImg ? null : undefined }, function (input) { dispatch({ type: 'capture', value: 'default' }, input.id); })];
      model.capture.forEach(function (c, i) {
        kids.push(choice({ id: name + '-' + (i + 1), name: name, value: c.sku, checked: cur === c.sku, label: c.label, desc: c.desc, sku: c.sku, price: c.price, list: c.compare_at, quote: c.sold ? null : str('by_quote'), image: withImg ? c.image : undefined }, function (input) { dispatch({ type: 'capture', value: c.sku }, input.id); }));
      });
      body.appendChild(fieldset(STEP_NAME.capture, null, kids, 'stack', true));
      body.appendChild(el('div', { class: 'bup-step__foot' }, [continueBtn('capture')]));
    }
    function renderAddons(body) {
      var base = 'bup-addon-' + cfg.sid;
      var plainAdd = model.addons.filter(function (a) { return !a.group; });
      var ilf = model.addons.filter(function (a) { return a.group === 'ilf'; });
      if (model.addons.length) body.appendChild(el('p', { class: 'bup-step__lead', text: str('addons_lead', { model: model.name }) }));
      if (plainAdd.length) body.appendChild(fieldset(STEP_NAME.addons, null, plainAdd.map(function (a, i) {
        return choice({ type: 'checkbox', id: base + '-' + i, name: base + '-' + a.sku, value: a.sku, checked: !!state.addons[a.sku], label: a.label, desc: a.desc, sku: a.sku, price: a.price, list: a.compare_at, quote: a.sold ? null : str('by_quote'), image: a.image }, function (input) { dispatch({ type: 'addon', sku: a.sku, on: input.checked }, input.id); });
      }), 'stack', true));
      if (ilf.length) {
        var opts = [choice({ id: base + '-ilf-0', name: base + '-ilf', value: '', checked: !state.ilf, label: str('ilf_none'), sub: str('ilf_none_sub'), compact: true }, function (input) { dispatch({ type: 'ilf', value: '' }, input.id); })];
        ilf.forEach(function (a, i) {
          opts.push(choice({ id: base + '-ilf-' + (i + 1), name: base + '-ilf', value: a.sku, checked: state.ilf === a.sku, label: a.label, sub: tileSub({ price: a.sold ? a.price : null, quote: !a.sold }), compact: true }, function (input) { dispatch({ type: 'ilf', value: a.sku }, input.id); }));
        });
        body.appendChild(fieldset(str('ilf_title'), str('ilf_desc'), opts, 'row'));
      }
      if (model.spare.length) {
        var allSold = model.spare.every(function (f) { return f.sold; });
        var total = model.spare.reduce(function (t, f) { return t + (f.price || 0); }, 0);
        var list = model.spare.reduce(function (t, f) { return t + (f.compare_at != null ? f.compare_at : f.price || 0); }, 0);
        body.appendChild(fieldset(str('spare_legend'), null, [choice({ type: 'checkbox', id: base + '-spare', name: base + '-spare', value: 'spare', checked: state.spare, label: spareTitle(model), desc: spareDesc(model), sku: model.spare.map(function (f) { return f.sku; }).join(' + '), price: allSold ? total : null, list: allSold && list > total ? list : null, quote: allSold ? null : str('by_quote'), image: model.spare[0].image }, function (input) { dispatch({ type: 'spare', on: input.checked }, input.id); })], 'stack', true));
        var note = filtersNote(model);
        if (note && cfg.filters_anchor) body.appendChild(el('p', { class: 'bup-step__ask' }, [note, el('a', { class: 'bup-link', href: cfg.filters_anchor, text: str('filters_link', { model: model.name }) }), '.']));
      }
      body.appendChild(el('div', { class: 'bup-step__foot' }, [continueBtn('addons')]));
    }
    function renderReview(body) {
      body.appendChild(el('p', { class: 'bup-step__lead', text: str(quoteBuild(model, state) ? 'review_lead_quote' : 'review_lead') }));
      var dl = el('dl', { class: 'bup-review', 'aria-label': str('review_title') });
      model.steps.filter(function (s) { return s !== 'review'; }).forEach(function (s) {
        dl.appendChild(el('div', { class: 'bup-review__row' }, [el('dt', { text: STEP_NAME[s] }), el('dd', { text: choiceText(model, state, s) || (s === 'capture' ? text(model.captureDefault) : str('review_none')) })]));
      });
      body.appendChild(dl);
      body.appendChild(actions('review'));
    }
    function renderSteps() {
      if (!stepsBox) return;
      clear(stepsBox);
      model.steps.forEach(function (step, i) {
        var sec = stepShell(step, i);
        if (state.open === step) {
          var body = el('div', { class: 'bup-step__body' });
          if (step === 'version') renderVersion(body);
          else if (step === 'hose') renderHose(body);
          else if (step === 'capture') renderCapture(body);
          else if (step === 'addons') renderAddons(body);
          else renderReview(body);
          sec.appendChild(body);
        }
        stepsBox.appendChild(sec);
      });
    }

    /* ---------- actions: Add system to cart + Request a quote ---------- */
    var status = qs(root, '[data-bup-status]');
    var sheet = qs(root, '[data-bup-sheet]');
    /* The steps and the summary are rebuilt on every change, so nothing here keeps a node: busy and the error are state. */
    function addSystem() {
      if (busy) return;
      var sum = summary(model, state);
      if (sum.blocker || !sum.cart.length) return;
      var items = sum.cart.map(function (l) { return { id: l.variant_id, quantity: 1 }; });
      busy = true;
      addError = '';
      renderActions();
      cartAdd(items).then(function (res) {
        busy = false;
        /* shown in the status line (where it is on screen), said once through the page's live region at every width */
        var done = items.length === 1 ? str('added_one', { model: model.name }) : str('added', { model: model.name, n: items.length });
        setText(status, done);
        announce(done);
        renderActions();
        if (sheet && sheet.open && sheet.close) sheet.close();
        openCart(res.key);
      }, function (e) { busy = false; addError = (e && e.message) || str('add_failed'); renderActions(); });
    }
    /* every copy of the actions (summary, review step, sheet) shows the same busy / error state */
    function renderActions() {
      var activeId = document.activeElement && root.contains(document.activeElement) ? document.activeElement.id : '';
      qsa(root, '[data-bup-actions]').forEach(function (old) { if (old.parentNode) old.parentNode.replaceChild(actions(old.getAttribute('data-bup-actions')), old); });
      /* the button just pressed was redrawn: give the focus back to it */
      var again = activeId ? document.getElementById(activeId) : null;
      if (again && document.activeElement !== again) again.focus({ preventScroll: true });
    }
    function actions(where) {
      var sum = summary(model, state);
      var wrap = el('div', { class: 'bup-actions', 'data-bup-actions': where });
      var err = el('p', { class: 'bup-error', role: 'alert', hidden: !addError, text: addError });
      if (!model.quoteOnly) {
        var blocked = !!sum.blocker;
        var whyId = 'bup-why-' + where + '-' + cfg.sid;
        var add = el('button', { type: 'button', class: 'bup-btn bup-btn--primary', id: 'bup-add-' + where + '-' + cfg.sid, 'data-bup-add': '', 'aria-disabled': blocked ? 'true' : null, 'aria-describedby': blocked ? whyId : null, 'aria-busy': busy ? 'true' : null }, [el('span', { 'data-slot': 'label', text: str(busy ? 'adding' : 'add') })]);
        add.addEventListener('click', function () { if (!summary(model, state).blocker) addSystem(); });
        wrap.appendChild(add);
        if (blocked) wrap.appendChild(el('p', { class: 'bup-actions__why', id: whyId, text: sum.blocker }));
      }
      wrap.appendChild(el('a', { class: 'bup-btn ' + (model.quoteOnly ? 'bup-btn--primary' : 'bup-btn--outline'), href: quoteHref(model, state), 'data-bup-quote': '', text: str('quote') }));
      wrap.appendChild(err);
      if (where === 'review') wrap.appendChild(el('p', { class: 'bup-actions__note', text: str('quote_note') }));
      return wrap;
    }

    /* ---------- "Your system" ---------- */
    var sumBox = qs(root, '[data-bup-summary]');
    var sheetBox = qs(root, '[data-bup-sheet-body]');
    function lineRow(l) {
      return el('li', { class: 'bup-line', 'data-sku': l.sku }, [
        el('span', { class: 'bup-line__text' }, [el('span', { class: 'bup-line__name', text: l.name }), el('span', { class: 'bup-line__meta', text: l.meta })]),
        l.priced ? el('span', { class: 'bup-line__money' }, [el('span', { class: 'bup-line__price', text: money(l.price) }), l.compare_at != null ? el('span', { class: 'bup-line__list', text: listRef(l.compare_at) }) : null]) : el('span', { class: 'bup-pill', text: str('by_quote') })
      ]);
    }
    function summaryContent(where) {
      var sum = summary(model, state);
      var frag = document.createDocumentFragment();
      if (!sum.lines.length) {
        frag.appendChild(el('ul', { class: 'bup-need' }, model.steps.filter(function (s) { return s === 'version' || s === 'hose'; }).map(function (s) { return el('li', { class: 'bup-need__row' }, [el('span', { text: (model.steps.indexOf(s) + 1) + ' · ' + STEP_NAME[s] }), el('span', { class: 'bup-need__tag', text: str('required').toLowerCase() })]); })));
        frag.appendChild(el('p', { class: 'bup-sum__empty', text: str('empty') }));
      } else {
        if (sum.main.length) frag.appendChild(el('ul', { class: 'bup-lines', 'data-bup-lines': 'main' }, sum.main.map(lineRow)));
        if (sum.included) frag.appendChild(el('p', { class: 'bup-sum__incl', text: str('included_line') }));
        if (sum.quote.length) {
          frag.appendChild(el('p', { class: 'bup-sum__qh', text: str('quote_group', { n: sum.quote.length }) }));
          frag.appendChild(el('ul', { class: 'bup-lines bup-lines--quote', 'data-bup-lines': 'quote' }, sum.quote.map(lineRow)));
          if (!sum.quoted) frag.appendChild(el('p', { class: 'bup-sum__qnote', text: str(sum.quote.length === 1 ? 'quote_group_note_one' : 'quote_group_note') }));
        }
      }
      var low = model.versions.reduce(function (m, v) { return m == null || v.price < m.price ? v : m; }, null);
      var total = el('div', { class: 'bup-total', 'data-bup-total': '' });
      if (sum.lines.length) {
        var countText = sum.quoted ? (sum.count === 1 ? str('items_quote_only_one') : str('items_quote_only', { n: sum.count })) : (sum.count === 1 ? str('items_cart_one') : str('items_cart', { n: sum.count }));
        total.appendChild(el('span', { class: 'bup-total__label' }, [el('span', { class: 'bup-total__name', text: str(sum.quoted ? 'build_total' : 'cart_total') }), el('span', { class: 'bup-total__count', text: countText })]));
        total.appendChild(el('span', { class: 'bup-total__money' }, [el('span', { class: 'bup-total__price', text: money(sum.total) }), sum.list != null ? el('span', { class: 'bup-total__list', text: listRef(sum.list) }) : null]));
      } else if (low) {
        total.appendChild(el('span', { class: 'bup-total__label' }, [el('span', { class: 'bup-total__name', text: str('from') })]));
        total.appendChild(el('span', { class: 'bup-total__money' }, [el('span', { class: 'bup-total__price', text: money(low.price) }), low.compare_at != null ? el('span', { class: 'bup-total__list', text: listRef(low.compare_at) }) : null]));
      }
      frag.appendChild(total);
      if (model.quoteOnly && cfg.power_note) frag.appendChild(el('p', { class: 'bup-sum__power', text: cfg.power_note }));
      frag.appendChild(actions(where));
      return frag;
    }
    var tiles = qs(root, '[data-bup-tiles]');
    var figcap = qs(root, '[data-bup-figcap]');
    function renderSummary() {
      if (sumBox) { clear(sumBox); sumBox.appendChild(summaryContent('summary')); }
      if (sheetBox && sheet && sheet.open) { clear(sheetBox); sheetBox.appendChild(summaryContent('sheet')); }
      /* the build view: a numbered tile per chosen item; a required step not done yet shows dashed */
      if (!tiles) return;
      clear(tiles);
      var sum = summary(model, state);
      var names = [];
      model.steps.filter(function (s) { return s !== 'version' && s !== 'review'; }).forEach(function (s) {
        var n = model.steps.indexOf(s) + 1;
        var mine = sum.lines.filter(function (l) { return l.step === s; });
        if (s === 'hose' && !mine.length && !stepDone(model, state, 'hose')) tiles.appendChild(el('li', { class: 'bup-fig__tile bup-fig__tile--need' }, [el('span', { class: 'bup-fig__n', 'aria-hidden': 'true', text: String(n) }), el('span', { text: str('tile_need', { name: STEP_NAME.hose }) })]));
        mine.forEach(function (l) {
          var src = safeSrc(l.image);
          names.push(l.name);
          tiles.appendChild(el('li', { class: 'bup-fig__tile' }, [el('span', { class: 'bup-fig__n', 'aria-hidden': 'true', text: String(n) }), src ? el('img', { class: 'bup-fig__img', src: src, alt: '', loading: 'lazy' }) : null, el('span', { text: l.priced ? l.name : str('tile_quote', { name: l.name }) })]));
        });
      });
      show(tiles, tiles.children.length > 0);
      setText(figcap, names.length ? str('figcap', { model: model.name, list: names.join(', ') }) : model.name);
    }

    /* ---------- phone and tablet: bar + sheet ---------- */
    var bar = qs(root, '[data-bup-bar]');
    function renderBar() {
      if (!bar) return;
      var sum = summary(model, state);
      var bits = [];
      if (!sum.lines.length) bits.push(str('bar_empty'));
      else {
        bits.push(str(sum.quoted ? 'bar_items' : 'bar_cart', { n: sum.count }));
        if (sum.quote.length) bits.push(str('bar_quote', { n: sum.quote.length }));
      }
      setText(qs(bar, '[data-slot="line"]'), str('bar') + ' · ' + bits.join(' · '));
      setText(qs(bar, '[data-slot="total"]'), sum.lines.length ? money(sum.total) : '');
    }
    if (bar && sheet) {
      var opener = qs(bar, '[data-bup-view]');
      var closeSheet = function () { if (sheet.open && sheet.close) sheet.close(); else sheet.removeAttribute('open'); };
      if (opener) opener.addEventListener('click', function () {
        clear(sheetBox);
        if (sheet.showModal) sheet.showModal(); else sheet.setAttribute('open', '');
        sheetBox.appendChild(summaryContent('sheet'));
      });
      qsa(sheet, '[data-bup-sheet-close]').forEach(function (b) { b.addEventListener('click', closeSheet); });
      sheet.addEventListener('click', function (e) { if (e.target === sheet) closeSheet(); });
      sheet.addEventListener('close', function () { clear(sheetBox); if (opener) opener.focus(); });
    }

    /* ---------- "Your build" / "Photos (n)" ---------- */
    var tabs = qsa(root, '[data-bup-view-tab]');
    var panes = qsa(root, '[data-bup-view-pane]');
    function showPane(which) {
      tabs.forEach(function (o) { o.setAttribute('aria-pressed', o.getAttribute('data-bup-view-tab') === which ? 'true' : 'false'); });
      panes.forEach(function (p) { show(p, p.getAttribute('data-bup-view-pane') === which); });
    }
    tabs.forEach(function (t) { t.addEventListener('click', function () { showPane(t.getAttribute('data-bup-view-tab')); }); });
    if (find(panes, function (p) { return p.getAttribute('data-bup-view-pane') === 'build'; })) showPane('build');

    function render() {
      renderTracker();
      renderSteps();
      renderSummary();
      renderBuy(versionOf(model, state));
      renderBar();
    }
    render();
    /* Back / Forward can bring this page back exactly as it was left (the browser's page cache), while the stored build
       has moved on in another page of the same tab: show the stored build. */
    window.addEventListener('pageshow', function (e) {
      if (!e || !e.persisted) return;
      var latest = initialState(store, cfg.store_key, model, true);
      latest.note = null;
      if (JSON.stringify(latest) === JSON.stringify(state)) return;
      state = latest;
      addError = '';
      render();
      syncUrl(model, versionOf(model, state));
    });
  }

  return {
    mount: mount, STR: STR, STEP_NAME: STEP_NAME, fill: fill, formatMoney: formatMoney, buildModel: buildModel, emptyState: emptyState, act: act, hoseView: hoseView, kitOf: kitOf,
    stepDone: stepDone, stepStatus: stepStatus, canOpen: canOpen, blocker: blocker, quoteBuild: quoteBuild, listOf: listOf, lines: lines, summary: summary, cartItems: cartItems, quoteHref: quoteHref,
    choiceText: choiceText, versionLine: versionLine, spareDesc: spareDesc, filtersNote: filtersNote, saveState: saveState, loadState: loadState, initialState: initialState, navType: navType
  };
});
